import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Users,
  RefreshCw,
  Search,
  Phone,
  Megaphone,
  MessageCircle,
  Sparkles,
  BarChart3,
  Ban,
  Trash2,
  X,
  TrendingUp,
  Calendar,
  Filter,
  Bell,
  StickyNote,
  Plus,
  CheckCircle2,
  Upload,
  FileText,
  Download,
  Pencil,
} from 'lucide-react';
import {
  AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { leadsApi } from '../services/api';
import { useLanguage } from '../hooks/useLanguage';
import './LeadsPage.css';

/**
 * Lead qualification rules (for this store's WhatsApp number):
 *  1) New messenger  → conversation has ≤ 3 messages total
 *  2) Ad reply       → inbound message was a reply to a WhatsApp ad / CTWA
 *
 * Until live detection is wired end-to-end, we ship a rich board seeded with
 * dummy leads for the store phone 0545834601 (+972545834601).
 */
export const isLeadCandidate = ({ messageCount = 0, isAdReply = false } = {}) =>
  Boolean(isAdReply) || Number(messageCount) <= 3;

const STORE_PHONE_DISPLAY = '0545834601';
const STORE_PHONE_E164 = '+972545834601';
const LEAD_META_KEY = 'rodbot_dummy_lead_meta_v1';

const STATUS_META = {
  new: { color: '#0073ea', labelKey: 'leads.statusNew' },
  contacted: { color: '#fdab3d', labelKey: 'leads.statusContacted' },
  converted: { color: '#00c875', labelKey: 'leads.statusConverted' },
  blocked: { color: '#e2445c', labelKey: 'leads.statusBlocked' },
};

const SOURCE_META = {
  new_messenger: { color: '#a25ddc', icon: MessageCircle, labelKey: 'leads.sourceNewMessenger' },
  ad_reply: { color: '#ff642e', icon: Megaphone, labelKey: 'leads.sourceAdReply' },
};

const hoursAgo = (h) => new Date(Date.now() - h * 3600 * 1000).toISOString();
const daysAgo = (d) => new Date(Date.now() - d * 86400 * 1000).toISOString();

/** Dummy leads for store 0545834601 — board seed data */
const DUMMY_LEADS = [
  {
    id: 'd1',
    store_phone: STORE_PHONE_E164,
    name: 'יוסי כהן',
    phone: '+972501234567',
    status: 'new',
    source: 'ad_reply',
    message_count: 1,
    ai_summary: 'הגיע ממודעת פייסבוק על טיפולי לייזר. שאל על מחיר לטיפול ראשון.',
    last_message: 'שלום, ראיתי את המודעה שלכם — כמה עולה טיפול?',
    last_message_time: hoursAgo(1),
    created_at: hoursAgo(1),
    owner: 'سارة',
  },
  {
    id: 'd2',
    store_phone: STORE_PHONE_E164,
    name: 'נועה לוי',
    phone: '+972523456789',
    status: 'new',
    source: 'new_messenger',
    message_count: 2,
    ai_summary: 'מספר חדש, שאלה על שעות פתיחה ומיקום.',
    last_message: 'אתם פתוחים היום עד מתי?',
    last_message_time: hoursAgo(3),
    created_at: hoursAgo(4),
    owner: '—',
  },
  {
    id: 'd3',
    store_phone: STORE_PHONE_E164,
    name: 'أحمد منصور',
    phone: '+972547891234',
    status: 'contacted',
    source: 'ad_reply',
    message_count: 5,
    ai_summary: 'رد على إعلان إنستغرام. مهتم بحجز موعد يوم الخميس.',
    last_message: 'تمام، بدي أحجز ليوم الخميس',
    last_message_time: hoursAgo(6),
    created_at: daysAgo(1),
    owner: 'سارة',
  },
  {
    id: 'd4',
    store_phone: STORE_PHONE_E164,
    name: 'מיכל אברהם',
    phone: '+972529876543',
    status: 'contacted',
    source: 'new_messenger',
    message_count: 3,
    ai_summary: 'שיחה קצרה — ביקשה המלצה על חבילת טיפולים.',
    last_message: 'יש לכם חבילה זוגית?',
    last_message_time: hoursAgo(12),
    created_at: daysAgo(1),
    owner: 'דני',
  },
  {
    id: 'd5',
    store_phone: STORE_PHONE_E164,
    name: 'דוד שמעון',
    phone: '+972501112233',
    status: 'converted',
    source: 'ad_reply',
    message_count: 8,
    ai_summary: 'הזמין טיפול מלא דרך מודעת גוגל. שולם מקדמה.',
    last_message: 'מעולה, נתראה ביום ראשון!',
    last_message_time: daysAgo(2),
    created_at: daysAgo(3),
    owner: 'דני',
  },
  {
    id: 'd6',
    store_phone: STORE_PHONE_E164,
    name: 'לין ח׳ליל',
    phone: '+972544455566',
    status: 'converted',
    source: 'new_messenger',
    message_count: 2,
    ai_summary: 'הגיעה דרך המלצה. קבעה תור תוך דקות.',
    last_message: 'תודה רבה!',
    last_message_time: daysAgo(2),
    created_at: daysAgo(2),
    owner: 'سارة',
  },
  {
    id: 'd7',
    store_phone: STORE_PHONE_E164,
    name: 'רועי גולן',
    phone: '+972502223344',
    status: 'blocked',
    source: 'new_messenger',
    message_count: 1,
    ai_summary: 'ספאם / לא רלוונטי — נחסם ידנית.',
    last_message: '...',
    last_message_time: daysAgo(4),
    created_at: daysAgo(4),
    owner: '—',
  },
  {
    id: 'd8',
    store_phone: STORE_PHONE_E164,
    name: 'סמאח נאסר',
    phone: '+972548887766',
    status: 'new',
    source: 'ad_reply',
    message_count: 1,
    ai_summary: 'رد على إعلان واتساب CTWA — طلبت تفاصيل عن العرض.',
    last_message: 'مرحبا، بدي تفاصيل العرض من الإعلان',
    last_message_time: hoursAgo(0.5),
    created_at: hoursAgo(0.5),
    owner: '—',
  },
];

const ConfirmModal = ({ open, title, message, confirmLabel, confirmColor, icon: Icon, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="ld-modal-overlay" onClick={onCancel}>
      <div className="ld-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ld-modal-close" onClick={onCancel}><X size={18} /></button>
        <div className="ld-modal-icon" style={{ color: confirmColor }}>
          {Icon && <Icon size={36} />}
        </div>
        <h3 className="ld-modal-title">{title}</h3>
        <p className="ld-modal-message">{message}</p>
        <div className="ld-modal-actions">
          <button className="ld-modal-btn ld-modal-cancel" onClick={onCancel}>✕</button>
          <button
            className="ld-modal-btn ld-modal-confirm"
            style={{ background: confirmColor }}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const LeadEditorModal = ({
  lead,
  meta,
  noteDraft,
  setNoteDraft,
  missionTitle,
  setMissionTitle,
  missionDue,
  setMissionDue,
  detailDraft,
  setDetailDraft,
  onSaveDetails,
  onSaveNote,
  onAddMission,
  onToggleMission,
  onUpdateMission,
  onDeleteMission,
  onAddExtraNote,
  onUpdateExtraNote,
  onDeleteExtraNote,
  onAddDocument,
  onDeleteDocument,
  statusLabel,
  statusOptions,
  onRequestClose,
  t,
}) => {
  // Draft state for adding an extra (non-main) note and for editing a mission
  // in place. Kept inside the modal so it resets naturally per open.
  const [newExtraNote, setNewExtraNote] = useState('');
  const [editingMissionId, setEditingMissionId] = useState(null);
  const [editMissionTitle, setEditMissionTitle] = useState('');
  const [editMissionDue, setEditMissionDue] = useState('');

  const leadId = lead?.id;
  useEffect(() => {
    // Reset transient drafts whenever a different lead is opened.
    setNewExtraNote('');
    setEditingMissionId(null);
  }, [leadId]);

  if (!lead) return null;
  const missions = meta?.missions || [];
  const extraNotes = meta?.extraNotes || [];

  const beginEditMission = (mission) => {
    setEditingMissionId(mission.id);
    setEditMissionTitle(mission.title);
    // datetime-local expects a local "YYYY-MM-DDTHH:mm" value.
    const d = new Date(mission.dueAt);
    const pad = (n) => String(n).padStart(2, '0');
    setEditMissionDue(
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
  };

  const saveEditMission = () => {
    if (!editMissionTitle.trim() || !editMissionDue) return;
    onUpdateMission(editingMissionId, {
      title: editMissionTitle.trim(),
      dueAt: new Date(editMissionDue).toISOString(),
    });
    setEditingMissionId(null);
  };

  const addExtraNote = () => {
    if (!newExtraNote.trim()) return;
    onAddExtraNote(newExtraNote);
    setNewExtraNote('');
  };
  return (
    <div className="ld-modal-overlay" onClick={onRequestClose}>
      <div className="ld-modal ld-lead-editor" onClick={(e) => e.stopPropagation()}>
        <div className="ld-editor-head">
          <div>
            <span className="ld-editor-kicker">{t('leads.leadWorkspace', 'Lead workspace')}</span>
            <h3>{lead.name || lead.phone}</h3>
            <span>{lead.phone}</span>
          </div>
          <button className="ld-modal-close" onClick={onRequestClose}><X size={18} /></button>
        </div>

        <div className="ld-editor-body">
          <div className="ld-editor-main">
            <section className="ld-editor-section">
          <div className="ld-editor-section-title">
            <Users size={16} />
            <span>{t('leads.leadDetails', 'Lead details')}</span>
          </div>
          <div className="ld-detail-grid">
            <label>
              <span>{t('leads.table.name')}</span>
              <input
                value={detailDraft.name}
                onChange={(e) => setDetailDraft((current) => ({ ...current, name: e.target.value }))}
              />
            </label>
            <label>
              <span>{t('leads.table.phone')}</span>
              <input
                dir="ltr"
                value={detailDraft.phone}
                onChange={(e) => setDetailDraft((current) => ({ ...current, phone: e.target.value }))}
              />
            </label>
            <label>
              <span>{t('leads.table.owner', 'Owner')}</span>
              <input
                value={detailDraft.owner}
                onChange={(e) => setDetailDraft((current) => ({ ...current, owner: e.target.value }))}
              />
            </label>
            <label>
              <span>{t('leads.table.status')}</span>
              <select
                value={detailDraft.status}
                onChange={(e) => setDetailDraft((current) => ({ ...current, status: e.target.value }))}
              >
                {statusOptions.map((status) => (
                  <option key={status.key} value={status.key}>{statusLabel(status.key)}</option>
                ))}
              </select>
            </label>
          </div>
          <button className="ld-editor-primary" onClick={onSaveDetails}>
            <CheckCircle2 size={14} />
            {t('leads.saveDetails', 'Save details')}
          </button>
            </section>

            <section className="ld-editor-section">
          <div className="ld-editor-section-title">
            <Sparkles size={16} />
            <span>{t('leads.aiSummary', 'AI WhatsApp summary')}</span>
          </div>
          {lead.ai_summary ? (
            <p className="ld-ai-summary" dir="auto">{lead.ai_summary}</p>
          ) : (
            <div className="ld-mission-empty">
              {t('leads.aiSummaryEmpty', 'No summary yet. It builds automatically from the WhatsApp chat when enabled in Settings.')}
            </div>
          )}
            </section>

            <section className="ld-editor-section">
          <div className="ld-editor-section-title">
            <StickyNote size={16} />
            <span>{t('leads.notes', 'Notes')}</span>
          </div>
          <label className="ld-note-label">{t('leads.mainNote', 'Main note')}</label>
          <textarea
            className="ld-note-textarea"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder={t('leads.notesPlaceholder', 'Add context, preferences, or follow-up details...')}
          />
          <button className="ld-editor-primary" onClick={onSaveNote}>
            <StickyNote size={14} />
            {t('leads.saveNote', 'Save main note')}
          </button>

          <div className="ld-extra-notes">
            <label className="ld-note-label">{t('leads.moreNotes', 'Additional notes')}</label>
            {extraNotes.length === 0 ? (
              <div className="ld-mission-empty">{t('leads.noExtraNotes', 'No additional notes yet')}</div>
            ) : (
              extraNotes.map((note) => (
                <div className="ld-extra-note" key={note.id}>
                  <textarea
                    className="ld-note-textarea ld-note-textarea-sm"
                    defaultValue={note.text}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if (v !== note.text) onUpdateExtraNote(note.id, v);
                    }}
                  />
                  <button
                    className="ld-row-btn ld-row-btn-delete"
                    onClick={() => onDeleteExtraNote(note.id)}
                    title={t('common.delete', 'Delete')}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))
            )}
            <div className="ld-extra-note-add">
              <textarea
                className="ld-note-textarea ld-note-textarea-sm"
                value={newExtraNote}
                onChange={(e) => setNewExtraNote(e.target.value)}
                placeholder={t('leads.addNotePlaceholder', 'Write another note...')}
              />
              <button className="ld-editor-primary ld-editor-primary-sm" onClick={addExtraNote} disabled={!newExtraNote.trim()}>
                <Plus size={14} />
                {t('leads.addNote', 'Add note')}
              </button>
            </div>
          </div>
            </section>
          </div>

          <div className="ld-editor-side">
            <section className="ld-editor-section">
          <div className="ld-editor-section-title">
            <Bell size={16} />
            <span>{t('leads.missions', 'Missions & reminders')}</span>
          </div>
          <div className="ld-mission-form">
            <input
              value={missionTitle}
              onChange={(e) => setMissionTitle(e.target.value)}
              placeholder={t('leads.missionPlaceholder', 'Call the lead, send an offer...')}
            />
            <input
              type="datetime-local"
              value={missionDue}
              onChange={(e) => setMissionDue(e.target.value)}
            />
            <button className="ld-editor-primary" onClick={onAddMission}>
              <Plus size={14} />
              {t('leads.addMission', 'Add mission')}
            </button>
          </div>

          <div className="ld-mission-list">
            {missions.length === 0 ? (
              <div className="ld-mission-empty">{t('leads.noMissions', 'No missions yet')}</div>
            ) : (
              missions
                .slice()
                .sort((a, b) => new Date(a.dueAt) - new Date(b.dueAt))
                .map((mission) =>
                  editingMissionId === mission.id ? (
                    <div className="ld-mission-item ld-mission-editing" key={mission.id}>
                      <div className="ld-mission-edit-form">
                        <input
                          value={editMissionTitle}
                          onChange={(e) => setEditMissionTitle(e.target.value)}
                          placeholder={t('leads.missionPlaceholder', 'Call the lead, send an offer...')}
                        />
                        <input
                          type="datetime-local"
                          value={editMissionDue}
                          onChange={(e) => setEditMissionDue(e.target.value)}
                        />
                        <div className="ld-mission-edit-actions">
                          <button className="ld-editor-primary ld-editor-primary-sm" onClick={saveEditMission}>
                            <CheckCircle2 size={13} />
                            {t('common.save', 'Save')}
                          </button>
                          <button className="ld-row-btn" onClick={() => setEditingMissionId(null)}>
                            {t('common.cancel', 'Cancel')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={`ld-mission-item ${mission.completed ? 'is-complete' : ''}`} key={mission.id}>
                      <button className="ld-mission-check" onClick={() => onToggleMission(mission.id)}>
                        <CheckCircle2 size={17} />
                      </button>
                      <div className="ld-mission-copy">
                        <strong>{mission.title}</strong>
                        <span>{new Date(mission.dueAt).toLocaleString()}</span>
                      </div>
                      <button className="ld-row-btn" onClick={() => beginEditMission(mission)} title={t('common.edit', 'Edit')}>
                        <Pencil size={13} />
                      </button>
                      <button className="ld-row-btn ld-row-btn-delete" onClick={() => onDeleteMission(mission.id)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )
                )
            )}
          </div>
            </section>

            <section className="ld-editor-section">
          <div className="ld-editor-section-title">
            <FileText size={16} />
            <span>{t('leads.documents', 'Documents')}</span>
          </div>
          <label className="ld-document-upload">
            <Upload size={15} />
            <span>{t('leads.addDocument', 'Add document')}</span>
            <input type="file" onChange={onAddDocument} />
          </label>
          <div className="ld-document-list">
            {(meta?.documents || []).length === 0 ? (
              <div className="ld-mission-empty">{t('leads.noDocuments', 'No documents yet')}</div>
            ) : (
              meta.documents.map((document) => (
                <div className="ld-document-item" key={document.id}>
                  <FileText size={16} />
                  <div>
                    <strong>{document.name}</strong>
                    <span>{Math.max(1, Math.round(document.size / 1024))} KB</span>
                  </div>
                  <a className="ld-row-btn" href={document.dataUrl} download={document.name} title="Download">
                    <Download size={13} />
                  </a>
                  <button className="ld-row-btn ld-row-btn-delete" onClick={() => onDeleteDocument(document.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ))
            )}
          </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Status manager: lets the client add their own lead statuses and recolour the
 * built-in ones. Built-in statuses (new/contacted/converted/blocked) keep their
 * translated labels and can't be deleted so the block automation stays intact.
 */
const StatusManagerModal = ({ statuses, onCreate, onUpdate, onDelete, onClose, statusLabel, t }) => {
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('#6366f1');
  const [busy, setBusy] = useState(false);

  const submitNew = async () => {
    const label = newLabel.trim();
    if (!label || busy) return;
    setBusy(true);
    const ok = await onCreate(label, newColor);
    setBusy(false);
    if (ok) {
      setNewLabel('');
      setNewColor('#6366f1');
    }
  };

  return (
    <div className="ld-modal-overlay" onClick={onClose}>
      <div className="ld-modal ld-status-manager" onClick={(e) => e.stopPropagation()}>
        <div className="ld-editor-head">
          <div>
            <span className="ld-editor-kicker">{t('leads.statusManager.kicker', 'Leads board')}</span>
            <h3>{t('leads.statusManager.title', 'Manage statuses')}</h3>
            <span>{t('leads.statusManager.subtitle', 'Add your own statuses or recolour the built-in ones')}</span>
          </div>
          <button className="ld-modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="ld-status-manager-body">
          <div className="ld-status-list">
            {statuses.map((s) => (
              <div className="ld-status-row" key={s.key}>
                <input
                  type="color"
                  className="ld-status-swatch"
                  value={s.color}
                  onChange={(e) => onUpdate(s.id, { color: e.target.value })}
                  title={t('leads.statusManager.color', 'Colour')}
                />
                {s.is_default ? (
                  <span className="ld-status-name ld-status-name-locked">{statusLabel(s.key)}</span>
                ) : (
                  <input
                    className="ld-status-name"
                    defaultValue={s.label}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== s.label) onUpdate(s.id, { label: v });
                    }}
                  />
                )}
                {s.is_default ? (
                  <span className="ld-status-tag">{t('leads.statusManager.builtin', 'Built-in')}</span>
                ) : (
                  <button
                    className="ld-status-del"
                    onClick={() => onDelete(s)}
                    title={t('common.delete', 'Delete')}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="ld-status-add">
            <input
              type="color"
              className="ld-status-swatch"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
            />
            <input
              className="ld-status-name"
              placeholder={t('leads.statusManager.newPlaceholder', 'New status name')}
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitNew(); }}
            />
            <button className="ld-status-add-btn" onClick={submitNew} disabled={busy || !newLabel.trim()}>
              <Plus size={14} />
              {t('leads.statusManager.add', 'Add')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const normalizeLead = (raw) => {
  const messageCount = Number(raw.message_count ?? 0);
  const isAdReply = Boolean(raw.is_ad_reply || raw.source === 'ad_reply');
  const source =
    raw.source ||
    (isAdReply ? 'ad_reply' : messageCount <= 3 ? 'new_messenger' : 'new_messenger');
  return {
    id: raw.id,
    store_phone: raw.store_phone || STORE_PHONE_E164,
    name: raw.name || '',
    phone: raw.phone || '',
    status: raw.status || 'new',
    source,
    message_count: messageCount,
    ai_summary: raw.ai_summary || '',
    last_message: raw.last_message || '',
    last_message_time: raw.last_message_time || raw.created_at || null,
    created_at: raw.created_at || null,
    owner: raw.owner || '—',
    is_dummy: Boolean(raw.is_dummy ?? String(raw.id).toString().startsWith('d')),
  };
};

const LeadsPage = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState(() => DUMMY_LEADS.map(normalizeLead));
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [confirmModal, setConfirmModal] = useState({ open: false, type: null, leadId: null });
  const [chartRange, setChartRange] = useState('daily');
  const [leadEditorId, setLeadEditorId] = useState(null);
  const [unsavedPrompt, setUnsavedPrompt] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [missionTitle, setMissionTitle] = useState('');
  const [missionDue, setMissionDue] = useState('');
  const [detailDraft, setDetailDraft] = useState({
    name: '',
    phone: '',
    owner: '',
    status: 'new',
  });
  const [leadMeta, setLeadMeta] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(LEAD_META_KEY) || '{}');
    } catch {
      return {};
    }
  });
  // Custom lead statuses loaded from the backend (defaults + client-created).
  const [statuses, setStatuses] = useState([]);
  const [statusManagerOpen, setStatusManagerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(LEAD_META_KEY, JSON.stringify(leadMeta));
  }, [leadMeta]);

  // Check scheduled missions and display an in-app popup (plus a native browser
  // notification when permission was granted).
  useEffect(() => {
    const checkReminders = () => {
      const now = Date.now();
      const due = [];
      Object.entries(leadMeta).forEach(([leadId, meta]) => {
        (meta.missions || []).forEach((mission) => {
          if (!mission.completed && !mission.notified && new Date(mission.dueAt).getTime() <= now) {
            due.push({ leadId, mission });
          }
        });
      });
      if (!due.length) return;

      due.forEach(({ leadId, mission }) => {
        const lead = leads.find((item) => String(item.id) === String(leadId));
        const title = `${t('leads.reminder', 'Reminder')}: ${mission.title}`;
        toast(title, {
          icon: '⏰',
          duration: 12000,
        });
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(t('leads.missionReminder', 'Lead mission reminder'), {
            body: `${lead?.name || lead?.phone || ''} — ${mission.title}`,
          });
        }
      });

      setLeadMeta((current) => {
        const next = { ...current };
        due.forEach(({ leadId, mission }) => {
          const key = String(leadId);
          next[key] = {
            ...(next[key] || {}),
            missions: (next[key]?.missions || []).map((item) =>
              item.id === mission.id ? { ...item, notified: true } : item
            ),
          };
        });
        return next;
      });
    };

    checkReminders();
    const timer = window.setInterval(checkReminders, 15000);
    return () => window.clearInterval(timer);
  }, [leadMeta, leads, t]);

  const openLeadEditor = (lead) => {
    setLeadEditorId(lead.id);
    setNoteDraft(leadMeta[String(lead.id)]?.notes || '');
    setDetailDraft({
      name: lead.name || '',
      phone: lead.phone || '',
      owner: lead.owner || '',
      status: lead.status || 'new',
    });
    setMissionTitle('');
    setMissionDue('');
  };

  const saveLeadDetails = () => {
    const key = String(leadEditorId);
    const details = {
      name: detailDraft.name.trim(),
      phone: detailDraft.phone.trim(),
      owner: detailDraft.owner.trim() || '—',
      status: detailDraft.status,
    };
    setLeads((current) =>
      current.map((lead) => (String(lead.id) === key ? { ...lead, ...details } : lead))
    );
    setLeadMeta((current) => ({
      ...current,
      [key]: { ...(current[key] || {}), details },
    }));
    toast.success(t('leads.detailsSaved', 'Lead details saved'));
  };

  const saveLeadNote = () => {
    const key = String(leadEditorId);
    setLeadMeta((current) => ({
      ...current,
      [key]: { ...(current[key] || {}), notes: noteDraft.trim() },
    }));
    toast.success(t('leads.noteSaved', 'Note saved'));
  };

  const addLeadMission = async () => {
    if (!missionTitle.trim() || !missionDue) {
      toast.error(t('leads.missionRequired', 'Add a mission and reminder time'));
      return;
    }
    const dueAt = new Date(missionDue).toISOString();
    const key = String(leadEditorId);
    const mission = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      title: missionTitle.trim(),
      dueAt,
      completed: false,
      notified: false,
    };
    setLeadMeta((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        missions: [...(current[key]?.missions || []), mission],
      },
    }));
    setMissionTitle('');
    setMissionDue('');
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
    toast.success(t('leads.missionAdded', 'Mission scheduled'));
  };

  const toggleLeadMission = (missionId) => {
    const key = String(leadEditorId);
    setLeadMeta((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        missions: (current[key]?.missions || []).map((mission) =>
          mission.id === missionId
            ? { ...mission, completed: !mission.completed, notified: mission.completed ? false : mission.notified }
            : mission
        ),
      },
    }));
  };

  const deleteLeadMission = (missionId) => {
    const key = String(leadEditorId);
    setLeadMeta((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        missions: (current[key]?.missions || []).filter((mission) => mission.id !== missionId),
      },
    }));
  };

  const updateLeadMission = (missionId, patch) => {
    const key = String(leadEditorId);
    setLeadMeta((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        missions: (current[key]?.missions || []).map((mission) =>
          // Rescheduling clears the "already notified" flag so the new time fires.
          mission.id === missionId ? { ...mission, ...patch, notified: false } : mission
        ),
      },
    }));
    toast.success(t('leads.missionUpdated', 'Reminder updated'));
  };

  // ─── Additional (non-main) notes ───
  // These persist to leadMeta immediately, so they don't participate in the
  // unsaved-changes guard (only the main note + details need an explicit save).
  const addExtraNote = (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    const key = String(leadEditorId);
    const note = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      text: trimmed,
      createdAt: new Date().toISOString(),
    };
    setLeadMeta((current) => ({
      ...current,
      [key]: { ...(current[key] || {}), extraNotes: [...(current[key]?.extraNotes || []), note] },
    }));
    toast.success(t('leads.noteAdded', 'Note added'));
  };

  const updateExtraNote = (id, text) => {
    const key = String(leadEditorId);
    setLeadMeta((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        extraNotes: (current[key]?.extraNotes || []).map((n) => (n.id === id ? { ...n, text } : n)),
      },
    }));
  };

  const deleteExtraNote = (id) => {
    const key = String(leadEditorId);
    setLeadMeta((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        extraNotes: (current[key]?.extraNotes || []).filter((n) => n.id !== id),
      },
    }));
  };

  // ─── Unsaved-changes guard for the workspace ───
  // Only the main note + lead details require an explicit save, so those are the
  // only things we diff against what's persisted.
  const isEditorDirty = () => {
    if (leadEditorId == null) return false;
    const key = String(leadEditorId);
    const lead = leads.find((l) => String(l.id) === key);
    if (!lead) return false;
    const savedMain = leadMeta[key]?.notes || '';
    const detailsDirty =
      detailDraft.name.trim() !== (lead.name || '') ||
      detailDraft.phone.trim() !== (lead.phone || '') ||
      (detailDraft.owner.trim() || '—') !== (lead.owner || '—') ||
      detailDraft.status !== (lead.status || 'new');
    const noteDirty = noteDraft.trim() !== savedMain.trim();
    return detailsDirty || noteDirty;
  };

  const closeEditor = () => {
    setLeadEditorId(null);
    setUnsavedPrompt(false);
  };

  const requestCloseEditor = () => {
    if (isEditorDirty()) {
      setUnsavedPrompt(true);
    } else {
      closeEditor();
    }
  };

  const saveAndCloseEditor = () => {
    saveLeadDetails();
    saveLeadNote();
    closeEditor();
  };

  const addLeadDocument = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('leads.documentTooLarge', 'Document must be smaller than 2 MB'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const key = String(leadEditorId);
      const document = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: file.name,
        type: file.type,
        size: file.size,
        dataUrl: reader.result,
        addedAt: new Date().toISOString(),
      };
      setLeadMeta((current) => ({
        ...current,
        [key]: {
          ...(current[key] || {}),
          documents: [...(current[key]?.documents || []), document],
        },
      }));
      toast.success(t('leads.documentAdded', 'Document added'));
    };
    reader.readAsDataURL(file);
  };

  const deleteLeadDocument = (documentId) => {
    const key = String(leadEditorId);
    setLeadMeta((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        documents: (current[key]?.documents || []).filter((document) => document.id !== documentId),
      },
    }));
  };

  const fetchLeads = useCallback(async () => {
    // For now: always seed the Monday board with dummy leads for 0545834601.
    // Live API merge comes next once webhook-side lead detection is wired.
    let storedMeta = {};
    try {
      storedMeta = JSON.parse(localStorage.getItem(LEAD_META_KEY) || '{}');
    } catch {
      storedMeta = {};
    }
    setLeads(
      DUMMY_LEADS.map(normalizeLead).map((lead) => ({
        ...lead,
        ...(storedMeta[String(lead.id)]?.details || {}),
      }))
    );
    setLoading(false);
    try {
      await leadsApi.listLeads(); // keep the route warm / auth-checked
    } catch (error) {
      console.warn('Leads API check skipped', error);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Load the store's status definitions (built-in + custom).
  const loadStatuses = useCallback(async () => {
    try {
      const res = await leadsApi.listStatuses();
      if (res.success && res.data?.statuses?.length) {
        setStatuses(res.data.statuses);
      }
    } catch (error) {
      console.warn('Could not load lead statuses', error);
    }
  }, []);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const createStatus = async (label, color) => {
    try {
      const res = await leadsApi.createStatus(label, color);
      if (res.success) {
        await loadStatuses();
        toast.success(t('leads.statusManager.created', 'Status added'));
        return true;
      }
      toast.error(res.error || t('common.error'));
    } catch {
      toast.error(t('common.error'));
    }
    return false;
  };

  const updateStatusDef = async (statusId, data) => {
    // Optimistic recolour/rename so the UI feels instant.
    setStatuses((prev) => prev.map((s) => (s.id === statusId ? { ...s, ...data } : s)));
    try {
      const res = await leadsApi.updateStatusDef(statusId, data);
      if (!res.success) {
        toast.error(res.error || t('common.error'));
        loadStatuses();
      }
    } catch {
      toast.error(t('common.error'));
      loadStatuses();
    }
  };

  const deleteStatusDef = async (status) => {
    try {
      const res = await leadsApi.deleteStatus(status.id);
      if (res.success) {
        await loadStatuses();
        // Leads that used it were moved back to "new" on the server.
        fetchLeads();
        toast.success(t('leads.statusManager.deleted', 'Status removed'));
      } else {
        toast.error(res.error || t('common.error'));
      }
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    const lead = leads.find((l) => l.id === leadId);
    // Optimistic UI for dummy + live
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
    if (lead?.is_dummy) {
      const key = String(leadId);
      setLeadMeta((current) => ({
        ...current,
        [key]: {
          ...(current[key] || {}),
          details: { ...(current[key]?.details || {}), status: newStatus },
        },
      }));
      return;
    }
    try {
      const response = await leadsApi.updateLeadStatus(leadId, newStatus);
      if (!response.success) {
        toast.error(response.error || t('common.error'));
        fetchLeads();
      }
    } catch {
      toast.error(t('common.error'));
      fetchLeads();
    }
  };

  const askBlock = (leadId) => setConfirmModal({ open: true, type: 'block', leadId });
  const askDelete = (leadId) => setConfirmModal({ open: true, type: 'delete', leadId });
  const closeModal = () => setConfirmModal({ open: false, type: null, leadId: null });

  const handleModalConfirm = async () => {
    const { type, leadId } = confirmModal;
    closeModal();
    const lead = leads.find((l) => l.id === leadId);

    if (type === 'block') {
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: 'blocked' } : l)));
      if (lead?.is_dummy) {
        toast.success(t('leads.block') + ' ✓');
        return;
      }
      try {
        const response = await leadsApi.blockLead(leadId);
        if (response.success) {
          toast.success(t('leads.block') + ' ✓');
        } else {
          toast.error(response.error || t('common.error'));
          fetchLeads();
        }
      } catch {
        toast.error(t('common.error'));
      }
    } else if (type === 'delete') {
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
      if (lead?.is_dummy) {
        toast.success(t('leads.delete') + ' ✓');
        return;
      }
      try {
        const response = await leadsApi.deleteLead(leadId);
        if (response.success) {
          toast.success(t('leads.delete') + ' ✓');
        } else {
          toast.error(response.error || t('common.error'));
          fetchLeads();
        }
      } catch {
        toast.error(t('common.error'));
      }
    }
  };

  const filteredLeads = useMemo(() => {
    let result = leads;
    if (statusFilter !== 'all') result = result.filter((l) => l.status === statusFilter);
    if (sourceFilter !== 'all') result = result.filter((l) => l.source === sourceFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (l) =>
          (l.name || '').toLowerCase().includes(q) ||
          (l.phone || '').includes(q) ||
          (l.ai_summary || '').toLowerCase().includes(q) ||
          (l.last_message || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [leads, statusFilter, sourceFilter, searchQuery]);

  const stats = useMemo(() => {
    const total = leads.length;
    const newCount = leads.filter((l) => l.status === 'new').length;
    const contacted = leads.filter((l) => l.status === 'contacted').length;
    const converted = leads.filter((l) => l.status === 'converted').length;
    const blocked = leads.filter((l) => l.status === 'blocked').length;
    const adReplies = leads.filter((l) => l.source === 'ad_reply').length;
    const newMessengers = leads.filter((l) => l.source === 'new_messenger').length;
    return { total, newCount, contacted, converted, blocked, adReplies, newMessengers };
  }, [leads]);

  const timeChartData = useMemo(() => {
    if (!leads.length) return [];
    const buckets = {};
    leads.forEach((l) => {
      const d = l.created_at ? new Date(l.created_at) : null;
      if (!d || isNaN(d)) return;
      const key =
        chartRange === 'monthly'
          ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
          : d.toISOString().slice(0, 10);
      buckets[key] = (buckets[key] || 0) + 1;
    });
    const sorted = Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, count]) => {
        let label;
        if (chartRange === 'monthly') {
          const [y, m] = key.split('-');
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          label = `${monthNames[parseInt(m, 10) - 1]} ${y.slice(2)}`;
        } else {
          const dt = new Date(key + 'T00:00:00');
          label = dt.toLocaleDateString(isRTL ? 'he-IL' : 'en-US', { month: 'short', day: 'numeric' });
        }
        return { key, label, count };
      });
    return chartRange === 'daily' && sorted.length > 30 ? sorted.slice(-30) : sorted;
  }, [leads, chartRange, isRTL]);

  const statusPieData = useMemo(() => {
    const entries = [
      { name: t('leads.statusNew'), value: stats.newCount, color: STATUS_META.new.color },
      { name: t('leads.statusContacted'), value: stats.contacted, color: STATUS_META.contacted.color },
      { name: t('leads.statusConverted'), value: stats.converted, color: STATUS_META.converted.color },
      { name: t('leads.statusBlocked'), value: stats.blocked, color: STATUS_META.blocked.color },
    ];
    return entries.filter((e) => e.value > 0);
  }, [stats, t]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString(isRTL ? 'he-IL' : 'en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString(isRTL ? 'he-IL' : 'en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Status list used across the board. Falls back to the four built-ins until
  // the backend list loads (or if it fails), so the UI never renders empty.
  const statusList = useMemo(() => {
    if (statuses.length) return statuses;
    return Object.keys(STATUS_META).map((key, i) => ({
      id: `def-${key}`, key, label: '', color: STATUS_META[key].color, is_default: true, position: i,
    }));
  }, [statuses]);

  const statusColorMap = useMemo(() => {
    const m = {};
    statusList.forEach((s) => { m[s.key] = s.color; });
    return m;
  }, [statusList]);

  const customLabelMap = useMemo(() => {
    const m = {};
    statusList.forEach((s) => { if (!s.is_default) m[s.key] = s.label; });
    return m;
  }, [statusList]);

  const statusColor = (key) => statusColorMap[key] || STATUS_META[key]?.color || '#7c8aa3';
  // Built-ins keep their translated labels; custom statuses use their stored name.
  const statusLabel = (s) => (STATUS_META[s] ? t(STATUS_META[s].labelKey) : (customLabelMap[s] || s));
  const sourceLabel = (s) => t(SOURCE_META[s]?.labelKey || s);

  if (loading) {
    return (
      <div className="ld-loading">
        <RefreshCw size={32} className="spin" />
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`ld-page ${isRTL ? 'ld-rtl' : ''}`}>
      <div className="ld-header">
        <div>
          <h1 className="ld-title">{t('leads.title')}</h1>
          <p className="ld-subtitle">
            {t('leads.boardSubtitle', 'Track and convert your WhatsApp leads')} ·{' '}
            <span className="ld-store-chip">
              <Phone size={12} /> {STORE_PHONE_DISPLAY}
            </span>
          </p>
        </div>
        <div className="ld-header-rules">
          <span className="ld-rule-pill ld-rule-new">
            <MessageCircle size={12} />
            {t('leads.ruleNewMessenger', '≤ 3 messages → lead')}
          </span>
          <span className="ld-rule-pill ld-rule-ad">
            <Megaphone size={12} />
            {t('leads.ruleAdReply', 'Ad reply → lead')}
          </span>
        </div>
      </div>

      {/* Tabs and the headline counts share one row so the page starts with a
          single compact band instead of three stacked strips. */}
      <div className="ld-subhead">
        <div className="ld-tabs">
          <button
            className={`ld-tab ${activeTab === 'leads' ? 'ld-tab-active' : ''}`}
            onClick={() => setActiveTab('leads')}
          >
            <Users size={16} />
            {t('leads.tabs.leads')}
          </button>
          <button
            className={`ld-tab ${activeTab === 'statistics' ? 'ld-tab-active' : ''}`}
            onClick={() => setActiveTab('statistics')}
          >
            <BarChart3 size={16} />
            {t('leads.tabs.statistics')}
          </button>
        </div>

        {activeTab === 'leads' && (
          <div className="ld-stat-cards">
            <div className="ld-stat-card">
              <Users size={16} />
              <div>
                <span className="ld-stat-value">{stats.total}</span>
                <span className="ld-stat-label">{t('leads.stats.totalLeads')}</span>
              </div>
            </div>
            <div className="ld-stat-card" style={{ borderColor: statusColor('new') }}>
              <span className="ld-dot" style={{ background: statusColor('new') }} />
              <div>
                <span className="ld-stat-value">{stats.newCount}</span>
                <span className="ld-stat-label">{statusLabel('new')}</span>
              </div>
            </div>
            <div className="ld-stat-card" style={{ borderColor: SOURCE_META.ad_reply.color }}>
              <Megaphone size={16} color={SOURCE_META.ad_reply.color} />
              <div>
                <span className="ld-stat-value">{stats.adReplies}</span>
                <span className="ld-stat-label">{t('leads.sourceAdReply')}</span>
              </div>
            </div>
            <div className="ld-stat-card" style={{ borderColor: SOURCE_META.new_messenger.color }}>
              <MessageCircle size={16} color={SOURCE_META.new_messenger.color} />
              <div>
                <span className="ld-stat-value">{stats.newMessengers}</span>
                <span className="ld-stat-label">{t('leads.sourceNewMessenger')}</span>
              </div>
            </div>
            <div className="ld-stat-card" style={{ borderColor: statusColor('converted') }}>
              <span className="ld-dot" style={{ background: statusColor('converted') }} />
              <div>
                <span className="ld-stat-value">{stats.converted}</span>
                <span className="ld-stat-label">{statusLabel('converted')}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {activeTab === 'leads' && (
        <>
          <div className="ld-toolbar">
            <div className="ld-search">
              <Search size={16} />
              <input
                type="text"
                placeholder={t('leads.table.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="ld-toolbar-right">
              <div className="ld-filter-wrap">
                <Filter size={14} />
                <select
                  className="ld-status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">{t('leads.table.status')}: All</option>
                  {statusList.map((s) => (
                    <option key={s.key} value={s.key}>{statusLabel(s.key)}</option>
                  ))}
                </select>
              </div>
              <button
                className="ld-manage-statuses-btn"
                onClick={() => setStatusManagerOpen(true)}
                title={t('leads.statusManager.title', 'Manage statuses')}
              >
                <Plus size={14} />
                {t('leads.statusManager.button', 'Statuses')}
              </button>
              <select
                className="ld-status-filter"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <option value="all">{t('leads.table.source', 'Source')}: All</option>
                <option value="new_messenger">{sourceLabel('new_messenger')}</option>
                <option value="ad_reply">{sourceLabel('ad_reply')}</option>
              </select>
              <button className="ld-icon-btn" onClick={fetchLeads} title="Refresh">
                <RefreshCw size={18} />
              </button>
            </div>
          </div>

          {/* Leads board table */}
          <div className="ld-board">
            <div className="ld-board-scroll">
              <table className="ld-monday-table">
                <thead>
                  <tr>
                    <th className="ld-col-name">{t('leads.table.name')}</th>
                    <th>{t('leads.table.phone')}</th>
                    <th>{t('leads.table.source', 'Source')}</th>
                    <th>{t('leads.table.messages', 'Msgs')}</th>
                    <th>{t('leads.table.status')}</th>
                    <th>{t('leads.table.owner', 'Owner')}</th>
                    <th>{t('leads.notes', 'Notes')}</th>
                    <th>{t('leads.missions', 'Missions')}</th>
                    <th>{t('leads.table.lastMessage')}</th>
                    <th>{t('leads.table.lastContact')}</th>
                    <th className="ld-col-actions">{t('leads.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="ld-empty-cell">
                        <Users size={36} />
                        <p>{t('leads.noLeads')}</p>
                        <span>{t('leads.noLeadsDesc')}</span>
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map((lead) => {
                      const src = SOURCE_META[lead.source] || SOURCE_META.new_messenger;
                      const SrcIcon = src.icon;
                      const st = { color: statusColor(lead.status) };
                      return (
                        <tr
                          key={lead.id}
                          className="ld-row"
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            if (event.target.closest('button, a, input, select, textarea, label')) return;
                            openLeadEditor(lead);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') openLeadEditor(lead);
                          }}
                        >
                          <td className="ld-col-name">
                            <div className="ld-name-cell">
                              <div
                                className="ld-avatar-sm"
                                style={{ background: `${st.color}22`, color: st.color, borderColor: st.color }}
                              >
                                {(lead.name || '?').charAt(0).toUpperCase()}
                              </div>
                              <div className="ld-name-text">
                                <span className="ld-name">{lead.name || t('common.unknown')}</span>
                                {lead.ai_summary && (
                                  <span className="ld-summary-line" title={lead.ai_summary}>
                                    <Sparkles size={10} />
                                    {lead.ai_summary.length > 56
                                      ? lead.ai_summary.slice(0, 56) + '…'
                                      : lead.ai_summary}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="ld-phone-cell">{lead.phone}</span>
                          </td>
                          <td>
                            <span className="ld-source-pill" style={{ background: `${src.color}22`, color: src.color }}>
                              <SrcIcon size={12} />
                              {sourceLabel(lead.source)}
                            </span>
                          </td>
                          <td>
                            <span className={`ld-msg-count ${lead.message_count <= 3 ? 'ld-msg-hot' : ''}`}>
                              {lead.message_count}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="ld-current-status"
                              style={{ '--status-color': st.color }}
                              onClick={() => openLeadEditor(lead)}
                            >
                              <span />
                              {statusLabel(lead.status)}
                            </button>
                          </td>
                          <td>
                            <span className="ld-owner">{lead.owner}</span>
                          </td>
                          <td>
                            <button className="ld-inline-meta" onClick={() => openLeadEditor(lead)}>
                              <StickyNote size={13} />
                              <span>
                                {leadMeta[String(lead.id)]?.notes
                                  ? leadMeta[String(lead.id)].notes.slice(0, 24)
                                  : t('leads.addNote', 'Add note')}
                              </span>
                            </button>
                          </td>
                          <td>
                            <button className="ld-inline-meta" onClick={() => openLeadEditor(lead)}>
                              <Bell size={13} />
                              <span>
                                {(leadMeta[String(lead.id)]?.missions || []).filter((m) => !m.completed).length ||
                                  t('leads.addMission', 'Add')}
                              </span>
                            </button>
                          </td>
                          <td>
                            <span className="ld-last-msg" title={lead.last_message}>
                              {lead.last_message
                                ? lead.last_message.length > 42
                                  ? lead.last_message.slice(0, 42) + '…'
                                  : lead.last_message
                                : '—'}
                            </span>
                          </td>
                          <td>
                            <span className="ld-when">
                              {formatDate(lead.last_message_time)}
                              <small>{formatTime(lead.last_message_time)}</small>
                            </span>
                          </td>
                          <td className="ld-col-actions">
                            <div className="ld-row-actions">
                              <button
                                className="ld-row-btn"
                                title={t('leads.openWorkspace', 'Notes & missions')}
                                onClick={() => openLeadEditor(lead)}
                              >
                                <Bell size={14} />
                              </button>
                              {lead.status !== 'blocked' ? (
                                <button
                                  className="ld-row-btn ld-row-btn-block"
                                  title={t('leads.block')}
                                  onClick={() => askBlock(lead.id)}
                                >
                                  <Ban size={14} />
                                </button>
                              ) : (
                                <button
                                  className="ld-row-btn"
                                  title={t('leads.unblock')}
                                  onClick={() => handleStatusChange(lead.id, 'new')}
                                >
                                  <Users size={14} />
                                </button>
                              )}
                              <button
                                className="ld-row-btn ld-row-btn-delete"
                                title={t('leads.delete')}
                                onClick={() => askDelete(lead.id)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'statistics' && (
        <div className="ld-statistics">
          <div className="ld-stat-cards">
            <div className="ld-stat-card">
              <Users size={18} />
              <div>
                <span className="ld-stat-value">{stats.total}</span>
                <span className="ld-stat-label">{t('leads.stats.totalLeads')}</span>
              </div>
            </div>
            <div className="ld-stat-card">
              <span className="ld-dot" style={{ background: STATUS_META.new.color }} />
              <div>
                <span className="ld-stat-value">{stats.newCount}</span>
                <span className="ld-stat-label">{statusLabel('new')}</span>
              </div>
            </div>
            <div className="ld-stat-card">
              <span className="ld-dot" style={{ background: STATUS_META.converted.color }} />
              <div>
                <span className="ld-stat-value">{stats.converted}</span>
                <span className="ld-stat-label">{statusLabel('converted')}</span>
              </div>
            </div>
          </div>

          <div className="ld-charts-grid">
            <div className="ld-chart-card">
              <div className="ld-chart-header">
                <div className="ld-chart-title-row">
                  <TrendingUp size={18} />
                  <h3>{t('leads.stats.leadsOverTime')}</h3>
                </div>
                <div className="ld-chart-range-toggle">
                  <button
                    className={`ld-range-btn ${chartRange === 'daily' ? 'ld-range-active' : ''}`}
                    onClick={() => setChartRange('daily')}
                  >
                    <Calendar size={13} />
                    {t('leads.stats.daily')}
                  </button>
                  <button
                    className={`ld-range-btn ${chartRange === 'monthly' ? 'ld-range-active' : ''}`}
                    onClick={() => setChartRange('monthly')}
                  >
                    <BarChart3 size={13} />
                    {t('leads.stats.monthly')}
                  </button>
                </div>
              </div>
              <div className="ld-chart-body">
                {timeChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={timeChartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0073ea" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#0073ea" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          background: '#1e293b',
                          border: '1px solid #334155',
                          borderRadius: 8,
                          color: '#f8fafc',
                        }}
                        formatter={(v) => [v, t('leads.stats.leadsCreated')]}
                      />
                      <Area type="monotone" dataKey="count" stroke="#0073ea" strokeWidth={2.5} fill="url(#areaGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="ld-chart-empty">
                    <BarChart3 size={40} />
                    <p>{t('leads.noData')}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="ld-chart-card ld-chart-card-pie">
              <div className="ld-chart-header">
                <div className="ld-chart-title-row">
                  <BarChart3 size={18} />
                  <h3>{t('leads.stats.statusBreakdown')}</h3>
                </div>
              </div>
              <div className="ld-chart-body ld-chart-body-pie">
                {statusPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                        stroke="none"
                      >
                        {statusPieData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: '#1e293b',
                          border: '1px solid #334155',
                          borderRadius: 8,
                          color: '#f8fafc',
                        }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        iconSize={8}
                        formatter={(value) => (
                          <span style={{ color: '#e2e8f0', fontSize: '0.75rem' }}>{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="ld-chart-empty">
                    <BarChart3 size={40} />
                    <p>{t('leads.noData')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <LeadEditorModal
        lead={leads.find((lead) => lead.id === leadEditorId)}
        meta={leadMeta[String(leadEditorId)] || {}}
        noteDraft={noteDraft}
        setNoteDraft={setNoteDraft}
        missionTitle={missionTitle}
        setMissionTitle={setMissionTitle}
        missionDue={missionDue}
        setMissionDue={setMissionDue}
        detailDraft={detailDraft}
        setDetailDraft={setDetailDraft}
        onSaveDetails={saveLeadDetails}
        onSaveNote={saveLeadNote}
        onAddMission={addLeadMission}
        onToggleMission={toggleLeadMission}
        onUpdateMission={updateLeadMission}
        onDeleteMission={deleteLeadMission}
        onAddExtraNote={addExtraNote}
        onUpdateExtraNote={updateExtraNote}
        onDeleteExtraNote={deleteExtraNote}
        onAddDocument={addLeadDocument}
        onDeleteDocument={deleteLeadDocument}
        statusLabel={statusLabel}
        statusOptions={statusList}
        onRequestClose={requestCloseEditor}
        t={t}
      />

      {/* Unsaved-changes guard: fires when closing the workspace with pending
          edits to the lead details or main note. */}
      {unsavedPrompt && (
        <div className="ld-modal-overlay" onClick={() => setUnsavedPrompt(false)}>
          <div className="ld-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ld-modal-icon"><StickyNote size={22} /></div>
            <h3 className="ld-modal-title">{t('leads.unsavedTitle', 'Save changes?')}</h3>
            <p className="ld-modal-message">
              {t('leads.unsavedMessage', 'You have unsaved changes in this lead. Do you want to save them before closing?')}
            </p>
            <div className="ld-modal-actions">
              <button className="ld-modal-btn ld-modal-cancel" onClick={closeEditor}>
                {t('leads.discard', 'Discard')}
              </button>
              <button
                className="ld-modal-btn ld-modal-confirm"
                style={{ background: 'var(--color-accent-primary)' }}
                onClick={saveAndCloseEditor}
              >
                {t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {statusManagerOpen && (
        <StatusManagerModal
          statuses={statusList}
          statusLabel={statusLabel}
          onCreate={createStatus}
          onUpdate={updateStatusDef}
          onDelete={deleteStatusDef}
          onClose={() => setStatusManagerOpen(false)}
          t={t}
        />
      )}

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.type === 'block' ? t('leads.block') : t('leads.delete')}
        message={confirmModal.type === 'block' ? t('leads.confirmBlock') : t('leads.confirmDelete')}
        confirmLabel={confirmModal.type === 'block' ? t('leads.block') : t('leads.delete')}
        confirmColor="#e2445c"
        icon={confirmModal.type === 'block' ? Ban : Trash2}
        onConfirm={handleModalConfirm}
        onCancel={closeModal}
      />
    </div>
  );
};

export default LeadsPage;
